import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, DataTable, Select, formatDateTime, type ColumnDef } from '@buildbee/ui';
import { trpc, type RouterOutputs } from '../../lib/trpc';
import { actionDetail, actionLabel } from './nav';

type Event = RouterOutputs['platform']['audit']['list']['items'][number];

const GROUPS = [
  { value: '', label: 'Everything' },
  { value: 'platform.user_', label: 'Accounts (passwords, locks, sign-outs)' },
  { value: 'platform.super_admin_', label: 'Super admin access' },
  { value: 'platform.member_', label: 'Company teams' },
  { value: 'platform.invitation_', label: 'Invitations' },
  { value: 'platform.project_template_', label: 'Project templates' },
  { value: 'platform.estimate_template_', label: 'Estimate templates' },
  { value: 'platform.document_template_', label: 'Proposal and email templates' },
  { value: 'platform.templates_', label: 'Library installs and copies' },
  { value: 'platform.supplier', label: 'Suppliers' },
  { value: 'platform.plan_changed', label: 'Plans' },
  { value: 'platform.trial_extended', label: 'Trials' },
  { value: 'platform.workspace_', label: 'Suspensions' },
];

/** /admin/activity: every action BuildBee staff took, newest first. */
export function PlatformActivityPage() {
  const [action, setAction] = useState('');
  const query = trpc.platform.audit.list.useInfiniteQuery({ limit: 50, action: action || undefined }, { getNextPageParam: (p) => p.nextCursor ?? undefined });
  const rows = query.data?.pages.flatMap((p) => p.items);

  const columns: ColumnDef<Event, unknown>[] = [
    { id: 'createdAt', accessorKey: 'createdAt', header: 'When', cell: ({ row }) => <span className="figures whitespace-nowrap">{formatDateTime(row.original.createdAt)}</span> },
    { id: 'actor', accessorKey: 'actorEmail', header: 'Who' },
    { id: 'action', accessorKey: 'action', header: 'What', cell: ({ row }) => <span className="font-medium">{actionLabel(row.original.action)}</span> },
    {
      id: 'target',
      header: 'For',
      cell: ({ row }) => {
        const e = row.original;
        const detail = actionDetail(e);
        return (
          <span className="flex flex-col">
            <span className="text-muted-foreground">{detail || '—'}</span>
            <span className="flex gap-3 text-xs">
              {e.targetUserId && (
                <Link to="/admin/users/$userId" params={{ userId: e.targetUserId }} className="hover:underline">
                  Person
                </Link>
              )}
              {e.targetWorkspaceId && (
                <Link to="/admin/workspaces/$workspaceId" params={{ workspaceId: e.targetWorkspaceId }} className="hover:underline">
                  Company
                </Link>
              )}
            </span>
          </span>
        );
      },
    },
    { id: 'ip', accessorKey: 'ip', header: 'IP', cell: ({ row }) => <span className="figures text-muted-foreground">{row.original.ip ?? '—'}</span> },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Every change made from this admin, by whom and for which person or company. Entries can&apos;t be edited or deleted. Changes to a company also show in that company&apos;s own audit log as “BuildBee support”.
      </p>
      <Select aria-label="Show" className="sm:max-w-xs" value={action} options={GROUPS} onChange={(e) => setAction(e.target.value)} />
      <DataTable
        columns={columns}
        data={rows}
        loading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        searchable={false}
        paginate={false}
        getRowId={(e) => e.id}
        empty={{ title: 'No activity yet' }}
        footer={
          query.hasNextPage ? (
            <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              Load more
            </Button>
          ) : null
        }
      />
    </div>
  );
}
